// CBT Skills — full interactive content library.
// Trauma-informed, plain English, UK spelling. Educational only.

export type Flashcard = { front: string; back: string };

export type ScenarioOption = {
  text: string;
  correct: boolean;
  feedback: string;
};

export type ScenarioQuiz = {
  prompt: string;
  options: ScenarioOption[];
};

export interface CbtSkill {
  id: string;
  title: string;
  emoji: string;
  summary: string;
  /** 2–4 short paragraphs — the 5-minute read. */
  learn: string[];
  flashcards: Flashcard[];
  /** Prompt for the interactive worksheet / practice tab. */
  worksheetPrompt: string;
  scenario: ScenarioQuiz;
  reflectionPrompt: string;
}

export type CbtModuleId =
  | "foundations"
  | "thinking-patterns"
  | "challenging-thoughts"
  | "behaviour-change"
  | "core-beliefs"
  | "everyday-tools";

export interface CbtModule {
  id: CbtModuleId;
  label: string;
  emoji: string;
  tagline: string;
  intro: string;
  theme: "sage" | "sky" | "peach" | "lavender" | "gold";
  skills: CbtSkill[];
}

export const CBT_INTRO = `Cognitive Behavioural Therapy (CBT) is one of the most widely researched approaches for anxiety, depression, and trauma-related distress. At its heart is a simple idea: the way we think about a situation shapes how we feel and act. When we notice unhelpful patterns, we can gently practise new ones. These modules are educational — not a replacement for working with a therapist.`;

// ─────────────────────────────────────────────────────────────
// 1. FOUNDATIONS
// ─────────────────────────────────────────────────────────────
const foundations: CbtSkill[] = [
  {
    id: "what-is-cbt",
    title: "What is CBT?",
    emoji: "🧭",
    summary: "A practical, evidence-based way of understanding yourself.",
    learn: [
      "CBT stands for Cognitive Behavioural Therapy. It's built on one core idea: our thoughts, feelings, body sensations, and behaviours are all connected. Change one and the others shift too.",
      "CBT doesn't ask you to 'think positively' or ignore what you feel. It teaches you to notice what your mind is doing, gently question thoughts that make things harder, and try small experiments that build evidence for a different way of being.",
      "For trauma survivors, CBT is one of several tools — often used alongside body-based and relational approaches. It's especially helpful for the loud inner critic, the catastrophic 'what ifs', and the avoidance loops that keep pain in place.",
    ],
    flashcards: [
      { front: "🧭 What is CBT?", back: "A structured, evidence-based approach linking thoughts, feelings, body, and behaviour." },
      { front: "Core idea", back: "Change one part of the cycle and the others shift with it." },
      { front: "Not this", back: "Not 'think positive'. Not ignoring feelings. Not blaming yourself." },
      { front: "Best for", back: "Anxiety, low mood, self-criticism, avoidance, and unhelpful thinking loops." },
    ],
    worksheetPrompt: "In your own words: what do you already know about how your thoughts affect your mood? Where in your life have you noticed that link?",
    scenario: {
      prompt: "A friend tells you CBT is 'just positive thinking'. Which reply is closest to the truth?",
      options: [
        { text: "Yes — you basically replace bad thoughts with good ones.", correct: false, feedback: "Not quite. CBT isn't about forcing positivity — that often backfires. It's about noticing and questioning thoughts, then choosing a more balanced view." },
        { text: "It teaches you to notice thoughts and check them against the evidence.", correct: true, feedback: "Yes. CBT is about curiosity and evidence, not forced optimism." },
        { text: "It ignores emotions and only focuses on behaviour.", correct: false, feedback: "The 'B' is important, but emotions and thoughts are central too. All four parts of the cycle matter." },
        { text: "It's the only therapy that works for trauma.", correct: false, feedback: "CBT is one useful approach — trauma often needs body-based and relational work alongside it." },
      ],
    },
    reflectionPrompt: "What drew you to CBT today? What would 'a bit less struggle' look like in your week?",
  },
  {
    id: "cbt-cycle",
    title: "The CBT Cycle",
    emoji: "🔄",
    summary: "Thoughts → feelings → body → behaviour — and back again.",
    learn: [
      "The CBT cycle shows how four parts of your experience feed each other. A thought sparks a feeling. The feeling shows up in the body. The body pushes an action. The action confirms the thought. Round it goes.",
      "Example: You think 'they're annoyed with me.' You feel anxious. Your chest tightens. You avoid the conversation. Now the mind concludes: 'See — I was right to worry.' The loop tightens.",
      "The good news: you can step into the cycle anywhere. Change one part — a slower breath, a small behaviour experiment, a gentler thought — and the whole loop starts to soften.",
    ],
    flashcards: [
      { front: "🔄 Four parts", back: "Thoughts · Feelings · Body sensations · Behaviours." },
      { front: "How it feeds itself", back: "A thought sparks feeling → body reacts → behaviour follows → the thought looks 'proven'." },
      { front: "Where to intervene", back: "Anywhere. Even a small shift in one part changes the whole loop." },
      { front: "Trauma note", back: "In trauma, the body often reacts first. That's normal — start with regulating the body." },
    ],
    worksheetPrompt: "Pick a recent moment that felt hard. Map the four parts: What was the situation? Thought? Feeling? Body? Behaviour?",
    scenario: {
      prompt: "You feel anxious, avoid a message, then decide 'I'm rubbish at friendships.' Where did the loop start?",
      options: [
        { text: "In the friendship itself — it's broken.", correct: false, feedback: "The evidence isn't there yet. The loop was inside you — the avoidance made the story feel 'true'." },
        { text: "Anywhere — that's the point. The parts fed each other.", correct: true, feedback: "Exactly. Once the cycle is running, it's less about origin and more about where you can step in." },
        { text: "In your body — anxiety came out of nowhere.", correct: false, feedback: "Body sensations matter, but they usually pair with a thought. Look at both." },
        { text: "In the other person — they should have replied faster.", correct: false, feedback: "Even if there's a real issue, the CBT loop is about your side of the pattern — the part you can work with." },
      ],
    },
    reflectionPrompt: "Which part of the cycle do you notice most easily — thoughts, feelings, body, or behaviour? Which is hardest to spot?",
  },
  {
    id: "thoughts-feelings-behaviours",
    title: "Thoughts, Feelings & Behaviours",
    emoji: "🧩",
    summary: "Learn to tell the three apart — they're not the same thing.",
    learn: [
      "A thought is something your mind says. A feeling is an emotion — often one word like 'anxious', 'sad', 'ashamed'. A behaviour is what you do — checking your phone, cancelling plans, going quiet.",
      "Most of us blur them together. 'I feel like a failure' is actually a thought ('I'm a failure') plus a feeling (probably shame). Untangling them makes the situation workable.",
      "Practise naming each one in isolation. It sounds small, but it's the foundation for every other CBT skill.",
    ],
    flashcards: [
      { front: "💭 Thought", back: "Something the mind says. Usually a sentence." },
      { front: "❤️ Feeling", back: "An emotion. Usually one word — anxious, sad, ashamed, angry, hopeful." },
      { front: "🚶 Behaviour", back: "What you actually do. Observable from outside." },
      { front: "Common mix-up", back: "'I feel like a failure' = thought ('I'm a failure') + feeling (shame)." },
    ],
    worksheetPrompt: "Write three sentences about a hard moment today. Label each word: thought (T), feeling (F), or behaviour (B).",
    scenario: {
      prompt: "Which of these is a feeling, not a thought?",
      options: [
        { text: "'I always mess things up.'", correct: false, feedback: "That's a thought — a sentence the mind is saying. The feeling underneath might be shame or hopelessness." },
        { text: "'Sad.'", correct: true, feedback: "Yes — one word, an emotion. That's a feeling." },
        { text: "'They must think I'm boring.'", correct: false, feedback: "That's a thought — specifically mind-reading. The feeling underneath is often anxiety." },
        { text: "'I stayed in bed all day.'", correct: false, feedback: "That's a behaviour — something you did." },
      ],
    },
    reflectionPrompt: "What's one 'I feel that…' sentence you say often? Can you rewrite it as: 'The thought is… The feeling is…'?",
  },
  {
    id: "automatic-thoughts",
    title: "Automatic Thoughts",
    emoji: "⚡",
    summary: "The fast, unspoken thoughts that shape how you feel.",
    learn: [
      "Automatic thoughts are the quick, often unnoticed thoughts that pop up in response to a situation. They arrive so fast they can feel like facts.",
      "Examples: 'They're annoyed with me.' 'I'm going to mess this up.' 'I'm too much.' Each one carries a whole emotional charge before you've even had time to look at it.",
      "You can't stop them arriving. But you can learn to catch them, write them down, and ask: 'Is that a fact, or is that a fear?' That single question changes everything.",
    ],
    flashcards: [
      { front: "⚡ Automatic thought", back: "A fast, often unspoken thought triggered by a situation." },
      { front: "How to spot one", back: "Notice the emotion first, then ask: 'What was I just thinking?'" },
      { front: "The key question", back: "'Is that a fact, or is that a fear?'" },
      { front: "You can't stop them", back: "You can only catch them. That's enough." },
    ],
    worksheetPrompt: "Think of a moment today when your mood dipped. What automatic thought was passing through — even one you barely noticed?",
    scenario: {
      prompt: "You get a short reply to your text. Your stomach sinks. What's the most useful next move?",
      options: [
        { text: "Send three follow-up messages to check they're okay.", correct: false, feedback: "That's the behaviour the anxious thought is asking for. Try catching the thought first." },
        { text: "Pause and ask: 'What did I just think that made my stomach sink?'", correct: true, feedback: "Yes. Catching the automatic thought is the first step in every other CBT skill." },
        { text: "Tell yourself off for being 'too sensitive'.", correct: false, feedback: "That's another automatic thought — a judgement. Notice it, but don't act on it." },
        { text: "Assume they're angry and prepare an apology.", correct: false, feedback: "You've believed the thought without checking it. That's what the skill is trying to interrupt." },
      ],
    },
    reflectionPrompt: "What's one automatic thought that shows up often for you? What emotion does it usually bring?",
  },
];

// ─────────────────────────────────────────────────────────────
// 2. THINKING PATTERNS
// ─────────────────────────────────────────────────────────────
const thinkingPatterns: CbtSkill[] = [
  {
    id: "cognitive-distortions",
    title: "Cognitive Distortions",
    emoji: "🎭",
    summary: "Common thinking traps that make things feel worse than they are.",
    learn: [
      "Cognitive distortions are habits of thinking that twist reality. They aren't a moral failure — most of them developed as survival strategies when the world felt unsafe.",
      "The most common: all-or-nothing thinking, catastrophising, mind-reading, fortune telling, emotional reasoning, should-statements, labelling, and personalisation.",
      "You don't need to eliminate them. You just need to notice which one is running, name it, and let that awareness loosen its grip.",
    ],
    flashcards: [
      { front: "🎭 What are they?", back: "Habitual thinking patterns that distort reality — usually toward the worse." },
      { front: "Why we have them", back: "Often protective — the mind trying to keep you safe by expecting the worst." },
      { front: "The skill", back: "Notice and name — 'That's catastrophising' — rather than fighting the thought." },
      { front: "The 8 big ones", back: "All-or-nothing · Catastrophising · Mind reading · Fortune telling · Emotional reasoning · Should statements · Labelling · Personalisation." },
    ],
    worksheetPrompt: "Look back at yesterday. Which distortion showed up most for you? Write one example of what your mind said.",
    scenario: {
      prompt: "Your friend hasn't replied. You think 'they hate me.' Which distortion is loudest?",
      options: [
        { text: "Should statements.", correct: false, feedback: "That'd be 'they SHOULD have replied by now'. Different flavour." },
        { text: "Mind reading — assuming you know their thoughts.", correct: true, feedback: "Yes. You've filled in what they must be thinking without evidence." },
        { text: "Personalisation.", correct: false, feedback: "Close — personalisation is more 'it must be my fault they're upset'. Mind-reading is the deeper one here." },
        { text: "None — it's probably true.", correct: false, feedback: "Maybe — but the skill is asking 'what pattern is my mind running?', not 'am I right?'" },
      ],
    },
    reflectionPrompt: "Which distortion is your most familiar one? Where do you think you learned it?",
  },
  {
    id: "all-or-nothing",
    title: "All-or-Nothing Thinking",
    emoji: "⚫",
    summary: "'I failed once, so I'm a complete failure.'",
    learn: [
      "All-or-nothing thinking (also called black-and-white thinking) sees the world in extremes. Perfect or ruined. Loved or hated. Success or failure. There's no middle.",
      "It's exhausting because life rarely fits those boxes. Most days are 'partly good, partly hard'. Most people are 'sometimes safe, sometimes not'. Most efforts are 'a bit worked, a bit didn't'.",
      "The antidote is spectrum thinking: percentages, 'and', 'sometimes', 'part of'. 'The talk was 60% good and 40% wobbly' is more useful — and more true — than 'it was a disaster'.",
    ],
    flashcards: [
      { front: "⚫ The pattern", back: "Everything is one extreme or the other — no middle ground." },
      { front: "Signal words", back: "'Always', 'never', 'ruined', 'perfect', 'total', 'complete'." },
      { front: "Reality check", back: "Life is a spectrum. Most things are partly one, partly the other." },
      { front: "Reframe with", back: "Percentages · 'and' · 'sometimes' · 'part of'." },
    ],
    worksheetPrompt: "Take a recent 'I completely failed' or 'I'm always the one who…' thought. Rewrite it using a percentage or 'part of'.",
    scenario: {
      prompt: "You forget one thing on your shopping list. Which is the all-or-nothing version?",
      options: [
        { text: "'I forgot the bread — I'll grab it tomorrow.'", correct: false, feedback: "That's balanced thinking. Nice." },
        { text: "'I'm losing my mind — I can't do anything right.'", correct: true, feedback: "Yes. One forgotten item became 'I can't do anything right'. Classic all-or-nothing." },
        { text: "'Annoying — but the rest of the shop went fine.'", correct: false, feedback: "That's actually a great spectrum reframe." },
        { text: "'I should make a better list next time.'", correct: false, feedback: "That's problem-solving, not all-or-nothing." },
      ],
    },
    reflectionPrompt: "Where in your life do you most often think in extremes? What would a 'spectrum' version look like?",
  },
  {
    id: "catastrophising",
    title: "Catastrophising",
    emoji: "🌪",
    summary: "'This mistake will ruin everything.'",
    learn: [
      "Catastrophising jumps from a small event to the worst possible outcome. A twinge becomes a serious illness. A wobble at work becomes losing the job. A silence becomes the end of the relationship.",
      "It often has trauma logic behind it — if you've lived through real catastrophes, your mind learned to scan for the next one. That's not a flaw. It's a nervous system doing its job on old settings.",
      "The skill: ask 'what's the most likely outcome?' not 'what's the worst?'. Then ask 'and if the worst did happen, what would I actually do?' — most of the time, you'd cope.",
    ],
    flashcards: [
      { front: "🌪 The pattern", back: "Jumping from a small event to the worst possible outcome." },
      { front: "Trauma link", back: "Often learned when real catastrophes did happen. Not a flaw — an old setting." },
      { front: "Better question", back: "'What's most likely?' — not 'what's the worst?'" },
      { front: "Coping check", back: "'If the worst did happen, what would I actually do?' You usually have more resources than you think." },
    ],
    worksheetPrompt: "Write out your worst-case story. Then write: what's most likely? And: if the worst did happen, what would I actually do?",
    scenario: {
      prompt: "You send a slightly awkward email. Which is catastrophising?",
      options: [
        { text: "'That was a bit clunky — I'll write more clearly next time.'", correct: false, feedback: "That's balanced. No catastrophe there." },
        { text: "'They'll think I'm unprofessional, I'll get demoted, and eventually I'll lose my job.'", correct: true, feedback: "Yes — one email to a whole imagined future collapse." },
        { text: "'I hope they don't mind.'", correct: false, feedback: "That's mild worry, not catastrophising." },
        { text: "'I'll follow up tomorrow if I don't hear back.'", correct: false, feedback: "That's a plan. Well done." },
      ],
    },
    reflectionPrompt: "What's the catastrophic story your mind tells most often? What's the most likely outcome instead?",
  },
  {
    id: "mind-reading",
    title: "Mind Reading",
    emoji: "🧠",
    summary: "'They must think I'm stupid.'",
    learn: [
      "Mind reading is assuming you know what someone else is thinking — usually something bad about you. It's one of the most common thinking traps, and one of the most convincing.",
      "The mind fills in gaps with the worst option available, because a threat you can predict feels safer than one you can't. It's a protection strategy — even when it makes you miserable.",
      "The reality check: you cannot know what someone is thinking without evidence. And even people who love you are usually thinking about themselves, not evaluating you.",
    ],
    flashcards: [
      { front: "🧠 The pattern", back: "Assuming you know what someone else is thinking — usually badly of you." },
      { front: "Why we do it", back: "A threat you can predict feels safer than one you can't. Even a false prediction is comforting to the anxious brain." },
      { front: "Reality check", back: "You cannot know a thought without evidence. Full stop." },
      { front: "Useful reframe", back: "'They're probably thinking about themselves — like most humans, most of the time.'" },
    ],
    worksheetPrompt: "Write down one thing you're sure someone is thinking about you. Then list the actual evidence — what did they say or do?",
    scenario: {
      prompt: "Your colleague looks away during your update. Which response is the mind-reading?",
      options: [
        { text: "'They looked away — I wonder if they're tired.'", correct: false, feedback: "That's a curious guess, and you're holding it lightly. Nice." },
        { text: "'They think this is boring and I'm wasting their time.'", correct: true, feedback: "Yes. You've filled in the exact contents of their head, and the story is unkind to you." },
        { text: "'I'll ask if the update landed okay.'", correct: false, feedback: "That's evidence-gathering — the opposite of mind reading." },
        { text: "'Maybe they got a message.'", correct: false, feedback: "That's a benign hypothesis, held lightly. Not mind reading." },
      ],
    },
    reflectionPrompt: "Whose thoughts do you mind-read the most? What would it feel like to only trust what they've actually said?",
  },
  {
    id: "fortune-telling",
    title: "Fortune Telling",
    emoji: "🔮",
    summary: "'I already know tomorrow will go badly.'",
    learn: [
      "Fortune telling is predicting a bad outcome as if it's already happened. 'I'll fail the interview.' 'They won't like me.' 'It'll be a disaster.'",
      "The prediction feels like a fact — but it isn't. It's just a story about a future that hasn't happened yet. And often, believing the prediction shapes the behaviour that makes it more likely.",
      "The skill is a two-step: name it as a prediction ('my mind is guessing the future'), then run a small experiment ('let me see what actually happens'). Evidence beats prediction, every time.",
    ],
    flashcards: [
      { front: "🔮 The pattern", back: "Treating a bad prediction as if it's already fact." },
      { front: "Signal phrase", back: "'I know it'll…' — the certainty is the tell." },
      { front: "Skill step 1", back: "Name it: 'My mind is guessing the future.'" },
      { front: "Skill step 2", back: "Run the tiny experiment. Evidence beats prediction." },
    ],
    worksheetPrompt: "Write out one prediction your mind made this week. What actually happened, or what would help you find out?",
    scenario: {
      prompt: "You've got a party this weekend. Which thought is fortune telling?",
      options: [
        { text: "'I don't know many people there — it might be a bit awkward.'", correct: false, feedback: "That's a realistic acknowledgement, not a locked-in prediction." },
        { text: "'It's going to be miserable, no one will talk to me, I already know.'", correct: true, feedback: "Yes. Certainty about a future that hasn't happened yet." },
        { text: "'I'll go for an hour and see.'", correct: false, feedback: "That's a behaviour experiment — the antidote." },
        { text: "'I hope I meet someone friendly.'", correct: false, feedback: "That's a hope, not a prediction of doom." },
      ],
    },
    reflectionPrompt: "What's one prediction your mind is treating as fact right now? What tiny experiment could test it?",
  },
  {
    id: "emotional-reasoning",
    title: "Emotional Reasoning",
    emoji: "🌊",
    summary: "'I feel it, therefore it must be true.'",
    learn: [
      "Emotional reasoning uses a feeling as proof of a fact. 'I feel guilty, so I must have done something wrong.' 'I feel anxious, so this must be dangerous.' 'I feel unlovable, so I must be.'",
      "Feelings are important — but they're information about you, not verdicts about reality. Anxiety often shows up where there's no actual danger. Guilt often shows up where you did nothing wrong.",
      "The skill: separate the feeling from the fact. 'I feel X. That doesn't automatically mean Y. What's the actual evidence for Y?'",
    ],
    flashcards: [
      { front: "🌊 The pattern", back: "Using an emotion as proof of a fact." },
      { front: "Common ones", back: "'I feel guilty → I did wrong.' 'I feel anxious → it's dangerous.' 'I feel unlovable → I am.'" },
      { front: "Feelings are…", back: "Information about you, not verdicts about reality." },
      { front: "The separator", back: "'I feel X. What's the actual evidence for the conclusion I'm drawing?'" },
    ],
    worksheetPrompt: "Name one feeling you're treating as fact right now. Then write: 'The feeling is real. The conclusion needs evidence.'",
    scenario: {
      prompt: "You wake up feeling anxious about a family visit. Which is emotional reasoning?",
      options: [
        { text: "'I feel anxious — so something bad must be about to happen.'", correct: true, feedback: "Yes. Anxiety became proof of danger, with no evidence in the room." },
        { text: "'I feel anxious — maybe I need to move my body for ten minutes.'", correct: false, feedback: "That's using the feeling as information, not a verdict. Nice." },
        { text: "'I feel anxious — visits are hard for me and that makes sense.'", correct: false, feedback: "That's compassionate acknowledgement, not distortion." },
        { text: "'I feel anxious — I'll ring my sister before I go.'", correct: false, feedback: "That's a supportive plan. Not emotional reasoning." },
      ],
    },
    reflectionPrompt: "Which feeling do you most often treat as fact? What would change if you held it as information instead?",
  },
  {
    id: "should-statements",
    title: "Should Statements",
    emoji: "📏",
    summary: "'I should be over this by now.'",
    learn: [
      "Should statements are inner rules — often inherited — that measure you against an invisible standard. 'I should be further along.' 'I shouldn't need help.' 'I should feel differently by now.'",
      "The rules feel like truth but they're usually old messages: from parents, teachers, culture, or the traumatised parts of you that learned safety meant performing perfectly.",
      "The skill: swap 'should' for 'want to' or 'it would help me to'. Notice what changes. 'I should exercise' becomes 'I want to feel steadier — a walk would help.' Same behaviour, no shame attached.",
    ],
    flashcards: [
      { front: "📏 The pattern", back: "Inner rules that measure you against an invisible standard." },
      { front: "Signal words", back: "'Should', 'shouldn't', 'must', 'ought to', 'supposed to'." },
      { front: "Where they come from", back: "Old messages — family, culture, traumatised parts that learned safety = performance." },
      { front: "The swap", back: "'Should' → 'want to' / 'it would help me to'. Same action, no shame." },
    ],
    worksheetPrompt: "Write three 'should' statements you say to yourself. Rewrite each without the word 'should'. What changes?",
    scenario: {
      prompt: "'I should be over the breakup by now.' Which reframe is the CBT one?",
      options: [
        { text: "'I am over it — I'm just being weak.'", correct: false, feedback: "That's silencing the feeling — not a reframe." },
        { text: "'Grief doesn't run on a schedule. I'm where I am.'", correct: true, feedback: "Yes — the should is gone, the compassion is in." },
        { text: "'Everyone else moves on faster than me.'", correct: false, feedback: "That's comparison plus another 'should' underneath." },
        { text: "'I should try harder.'", correct: false, feedback: "That's another should stacked on top." },
      ],
    },
    reflectionPrompt: "What's one 'should' you carry that isn't actually yours? Whose voice does it sound like?",
  },
  {
    id: "labelling",
    title: "Labelling",
    emoji: "🏷️",
    summary: "'I'm a failure.' — the mind turns an event into an identity.",
    learn: [
      "Labelling takes a behaviour or event and turns it into an identity. 'I made a mistake' becomes 'I'm an idiot'. 'I lost my temper' becomes 'I'm a bad person'.",
      "Once the label sticks, everything gets filtered through it. It becomes very hard to see anything that contradicts it — and easy to see anything that confirms it.",
      "The skill: separate the doing from the being. 'I did X' or 'X happened' is workable. 'I am X' is a story your mind is telling — and it's rarely accurate.",
    ],
    flashcards: [
      { front: "🏷️ The pattern", back: "Turning a behaviour or event into an identity." },
      { front: "Example", back: "'I made a mistake' → 'I'm an idiot'. One is a fact; the other is a story." },
      { front: "Why it sticks", back: "Once a label lands, the mind filters for confirming evidence and ignores the rest." },
      { front: "The skill", back: "Separate doing from being. 'I did X' — never 'I am X'." },
    ],
    worksheetPrompt: "Write down one label you use for yourself. Now write the actual behaviour behind it — no identity words allowed.",
    scenario: {
      prompt: "You snapped at a friend. Which is the labelling response?",
      options: [
        { text: "'I snapped — I'll apologise later.'", correct: false, feedback: "Nice — separating the behaviour from the person." },
        { text: "'I'm a horrible person.'", correct: true, feedback: "Yes. One snap became a whole identity. Classic labelling." },
        { text: "'I was overwhelmed and it came out sideways.'", correct: false, feedback: "That's a compassionate acknowledgement of what happened." },
        { text: "'I need to check in with them tomorrow.'", correct: false, feedback: "That's a repair plan — no labelling in sight." },
      ],
    },
    reflectionPrompt: "What label do you most often give yourself? What would the 'behaviour version' of that be?",
  },
  {
    id: "personalisation",
    title: "Personalisation",
    emoji: "🎯",
    summary: "'It must be about me.' — taking everything personally.",
    learn: [
      "Personalisation is taking responsibility for things that aren't yours — a partner's mood, a friend's silence, a colleague's stress, the weather at the wedding.",
      "It's especially common for people who grew up as emotional caretakers. If your safety once depended on managing someone else's feelings, your mind still scans for what you need to fix.",
      "The reality: most of what happens to people around you has almost nothing to do with you. The skill is asking, 'is this actually about me — or am I assuming?'",
    ],
    flashcards: [
      { front: "🎯 The pattern", back: "Taking responsibility for things that aren't yours to carry." },
      { front: "Where it comes from", back: "Often learned in childhoods where your safety depended on managing others' moods." },
      { front: "Reality", back: "Most of what people feel has almost nothing to do with you." },
      { front: "The question", back: "'Is this actually about me — or am I assuming?'" },
    ],
    worksheetPrompt: "Name one thing you're currently blaming yourself for. What are all the other possible causes?",
    scenario: {
      prompt: "Your partner is grumpy after work. Which is personalisation?",
      options: [
        { text: "'They've had a long day — I'll give them space.'", correct: false, feedback: "That's separating their state from you. Nice." },
        { text: "'I must have done something wrong.'", correct: true, feedback: "Yes. Their mood became your fault — classic personalisation." },
        { text: "'I'll ask if they want tea.'", correct: false, feedback: "That's care, not personalisation." },
        { text: "'Work sounds hard for them lately.'", correct: false, feedback: "That's noticing their context, not making it about you." },
      ],
    },
    reflectionPrompt: "Whose feelings do you most often make your responsibility? What would it feel like to let them belong to that person?",
  },
];

// ─────────────────────────────────────────────────────────────
// 3. CHALLENGING THOUGHTS
// ─────────────────────────────────────────────────────────────
const challengingThoughts: CbtSkill[] = [
  {
    id: "thought-record",
    title: "Thought Record",
    emoji: "📝",
    summary: "The classic CBT worksheet — catch, examine, rebalance.",
    learn: [
      "A thought record is the workhorse of CBT. It slows a difficult moment down enough that you can see the thinking underneath, look at the evidence on both sides, and land on something more balanced.",
      "The seven columns: Situation → Automatic Thought → Emotion (0–100%) → Evidence For → Evidence Against → Balanced Thought → Emotion Now (0–100%). Most people find the emotion softens by 20–40% just from doing the record.",
      "Do it in writing, not in your head — the whole point is to externalise what your mind is doing so you can look at it, rather than being inside it.",
    ],
    flashcards: [
      { front: "📝 What it does", back: "Slows down a difficult moment so you can see and rebalance your thinking." },
      { front: "The 7 columns", back: "Situation · Thought · Emotion % · Evidence For · Evidence Against · Balanced Thought · Emotion Now %" },
      { front: "Why writing matters", back: "Externalising thoughts lets you look at them instead of being inside them." },
      { front: "Typical result", back: "Emotion often softens by 20–40% just from completing the record." },
    ],
    worksheetPrompt: "Complete a full thought record now. Answer each column: 1) Situation — what happened? 2) Automatic thought — what went through your mind? 3) Emotion + rating 0–100%. 4) Evidence supporting the thought — the facts. 5) Evidence against the thought — what else is true? 6) Balanced thought — a more realistic, compassionate view. 7) Emotion now — rate the intensity again.",
    scenario: {
      prompt: "You're mid-thought-record and can only find evidence FOR your thought. What's the CBT move?",
      options: [
        { text: "Give up — you must be right.", correct: false, feedback: "Not quite. 'Only for evidence' often means the mind is still looking through one lens." },
        { text: "Ask: 'What would I say to a friend in this situation?' Then write that.", correct: true, feedback: "Yes. Compassion often unlocks the evidence-against column when your own perspective can't." },
        { text: "Skip the against column.", correct: false, feedback: "That's the column doing the work. Try a different angle — friend's view, best-friend voice, or 'is there any other way to see this?'" },
        { text: "Write down every bad thing you can think of.", correct: false, feedback: "That's rumination, not a thought record." },
      ],
    },
    reflectionPrompt: "How did your emotion rating change from start to finish? What was the most useful line you wrote?",
  },
  {
    id: "evidence-for-and-against",
    title: "Evidence For & Against",
    emoji: "⚖️",
    summary: "Two columns. What the mind is saying, and what's actually true.",
    learn: [
      "Anxious thoughts are prosecutors — they only present evidence for the case. This skill invites you to be your own defence lawyer and gather the other side too.",
      "Evidence For: the actual facts (not feelings) that support the thought. Evidence Against: the actual facts (not hopes) that don't fit the story.",
      "The rule: only observable, provable evidence counts. 'I feel like a failure' isn't evidence — 'I finished the project' is.",
    ],
    flashcards: [
      { front: "⚖️ The idea", back: "Anxious thoughts only show you one side. This skill gathers both." },
      { front: "Evidence For", back: "Actual facts that support the thought — not feelings." },
      { front: "Evidence Against", back: "Actual facts that contradict it — not hopes." },
      { front: "The rule", back: "Only observable, provable evidence counts on either side." },
    ],
    worksheetPrompt: "Pick a sticky thought. Draw two columns. Fill in Evidence For, then Evidence Against — facts only, no feelings.",
    scenario: {
      prompt: "Thought: 'Nobody likes me.' Which line belongs in Evidence Against?",
      options: [
        { text: "'I feel unlovable.'", correct: false, feedback: "That's a feeling, not evidence. It goes in the emotion column." },
        { text: "'My sister rang me twice this week and I had two coffee meets.'", correct: true, feedback: "Yes. Observable facts, not opinions. That's evidence." },
        { text: "'I should try to make more friends.'", correct: false, feedback: "That's a 'should' — an instruction, not evidence." },
        { text: "'I always mess up friendships.'", correct: false, feedback: "That's another distorted thought, not evidence against the first one." },
      ],
    },
    reflectionPrompt: "Which side did your mind find easier — for or against? What does that tell you?",
  },
  {
    id: "alternative-thoughts",
    title: "Alternative Thoughts",
    emoji: "🔀",
    summary: "For every stuck thought, there are three or four other possibilities.",
    learn: [
      "When a thought locks in, it feels like the only reading of the situation. Alternative Thoughts is the practice of deliberately generating other plausible interpretations.",
      "Rule: they don't have to feel true yet. You're just widening the field so your mind stops treating one story as fact. Even 'this is possible' is enough at first.",
      "Aim for at least three alternatives. The third one often surprises you — it's usually the one your defended mind was hiding.",
    ],
    flashcards: [
      { front: "🔀 What it is", back: "Deliberately generating other plausible readings of the same event." },
      { front: "They don't have to feel true", back: "The point is to widen the field, not to force belief." },
      { front: "How many?", back: "At least three. The third one often surprises you." },
      { front: "Best question", back: "'What else could this mean — even if I don't believe it yet?'" },
    ],
    worksheetPrompt: "Take one automatic thought. List at least three alternative interpretations of the same situation.",
    scenario: {
      prompt: "Thought: 'My boss walked past without saying hi — she's angry with me.' Best alternative?",
      options: [
        { text: "'She's angry — I should apologise.'", correct: false, feedback: "That's the same story, not an alternative." },
        { text: "'She might have been distracted, on a call, or just having a rough morning.'", correct: true, feedback: "Yes — three other plausible readings, all of them likely." },
        { text: "'She hates me.'", correct: false, feedback: "That's a stronger version of the same story — the opposite of what this skill is for." },
        { text: "'It doesn't matter.'", correct: false, feedback: "Dismissing the feeling isn't an alternative reading — just a shutdown." },
      ],
    },
    reflectionPrompt: "Which alternative surprised you most? What would it feel like to hold it as possible for a whole day?",
  },
  {
    id: "reframing",
    title: "Reframing",
    emoji: "🖼️",
    summary: "Same situation, different lens — one that helps you move.",
    learn: [
      "Reframing means choosing a different, equally-true way of seeing something. It isn't lying or forcing positivity — it's picking a lens that lets you keep moving.",
      "Reframe examples: 'I failed' → 'I got useful data'. 'I'm too sensitive' → 'I feel deeply, which is often useful'. 'This is unbearable' → 'This is really hard, and hard things pass'.",
      "The test: does the new frame reduce shame and increase what you can do? If yes, keep it. If it's just denial dressed up nicely, drop it and try again.",
    ],
    flashcards: [
      { front: "🖼️ What it is", back: "Choosing a different, equally-true lens on the same situation." },
      { front: "Not this", back: "Not lying. Not forced positivity. Not denial." },
      { front: "The test", back: "Does it reduce shame AND increase what you can do? If yes, keep it." },
      { front: "Common reframes", back: "'Failed' → 'got data'. 'Too sensitive' → 'feels deeply'. 'Unbearable' → 'hard, and passing'." },
    ],
    worksheetPrompt: "Pick one sticky story you tell about yourself. Write three reframes. Which one loosens something?",
    scenario: {
      prompt: "'I keep making the same mistakes in relationships.' Best reframe?",
      options: [
        { text: "'I'm broken.'", correct: false, feedback: "That's a stronger version of the original story, not a reframe." },
        { text: "'Everything is fine actually.'", correct: false, feedback: "That's denial. Reframing keeps the truth of the difficulty." },
        { text: "'I'm learning where my patterns come from — that's what change looks like.'", correct: true, feedback: "Yes — same reality, different lens. It reduces shame and increases movement." },
        { text: "'I'll never learn.'", correct: false, feedback: "That's fortune telling stacked on labelling." },
      ],
    },
    reflectionPrompt: "What's one story about yourself that could use a new frame? What might that frame be?",
  },
  {
    id: "socratic-questioning",
    title: "Socratic Questioning",
    emoji: "❓",
    summary: "Ask, don't argue. The mind opens to questions it closes to correction.",
    learn: [
      "Socratic questioning is a set of gentle, curious questions that help you examine a thought without attacking it. It's named after Socrates because he taught by asking, not telling.",
      "The core questions: Is this thought a fact or an opinion? What's the evidence? What would I tell a friend? What's the worst / best / most likely outcome? How will I feel about this in a week?",
      "The trick is tone. Ask like a curious friend, not a prosecutor. The nervous system opens to curiosity and closes to correction.",
    ],
    flashcards: [
      { front: "❓ What it is", back: "Gentle, curious questions that help you examine a thought — no argument." },
      { front: "Fact or opinion?", back: "Almost every 'I am…' or 'they think…' statement is opinion, not fact." },
      { front: "Friend test", back: "'What would I say to a friend having this thought?'" },
      { front: "Perspective test", back: "'How will I feel about this in a week? A month? A year?'" },
    ],
    worksheetPrompt: "Pick a stuck thought. Answer each: Fact or opinion? Evidence? Friend view? Most-likely outcome? A week from now?",
    scenario: {
      prompt: "Your friend says 'I'm useless.' Which is the Socratic move?",
      options: [
        { text: "'No you're not — stop saying that.'", correct: false, feedback: "That's correction. The mind usually rejects it." },
        { text: "'What would useless actually look like — and what did you do this week?'", correct: true, feedback: "Yes. Curious question, gentle challenge, no argument." },
        { text: "'You should think positive.'", correct: false, feedback: "That's a should stacked on a shutdown." },
        { text: "'Yeah, you're a mess.'", correct: false, feedback: "That's collusion, not questioning." },
      ],
    },
    reflectionPrompt: "Which Socratic question lands hardest for you? Which one do you avoid asking yourself?",
  },
  {
    id: "balanced-thinking",
    title: "Balanced Thinking",
    emoji: "⚖️",
    summary: "Not positive, not negative — accurate.",
    learn: [
      "Balanced thinking is the goal of most CBT work. It's neither the anxious worst-case nor forced sunshine — it's what actually seems true when you hold all the evidence at once.",
      "A balanced thought includes what's hard AND what's true AND what's workable. 'This is really painful, and I've survived hard things before, and I have one small thing I can do today.'",
      "It shouldn't feel like a lie. If it does, it's not balanced yet — it's positive-spin, which the trauma-informed part of you will reject. Rework it until it feels honest.",
    ],
    flashcards: [
      { front: "⚖️ What it is", back: "Neither worst-case nor forced positivity — what's actually true when you hold all evidence at once." },
      { front: "The shape", back: "'This is hard AND this is true AND here is one thing I can do.'" },
      { front: "The test", back: "Does it feel honest? If it feels like a lie, it's not balanced yet." },
      { front: "Why 'AND'", back: "Two truths together — never 'but'. 'But' cancels the first half." },
    ],
    worksheetPrompt: "Take a hard thought. Write a balanced version using 'AND' three times. Read it aloud — does it feel honest?",
    scenario: {
      prompt: "'I had a hard day at work.' Which is balanced?",
      options: [
        { text: "'I'm terrible at my job and I always will be.'", correct: false, feedback: "That's the worst-case, not balance." },
        { text: "'Everything is fine — I'm blessed to have work.'", correct: false, feedback: "That's forced positivity — it dismisses what's true." },
        { text: "'Today was hard AND I got through it AND I need rest tonight.'", correct: true, feedback: "Yes — hard truth, evidence of coping, one workable next step." },
        { text: "'I'll just push through.'", correct: false, feedback: "That's avoidance dressed up as resilience." },
      ],
    },
    reflectionPrompt: "What's one balanced thought you'd like to keep close this week?",
  },
];

// ─────────────────────────────────────────────────────────────
// 4. BEHAVIOUR CHANGE
// ─────────────────────────────────────────────────────────────
const behaviourChange: CbtSkill[] = [
  {
    id: "behavioural-activation",
    title: "Behavioural Activation",
    emoji: "🌤",
    summary: "Do first, feel second. Small actions gently lift mood.",
    learn: [
      "When mood is low, motivation goes with it. Waiting to 'feel like it' before doing anything traps you. Behavioural activation flips the order: do the small thing, then let the feeling catch up.",
      "Start tiny. Not 'go for a run' — 'put trainers by the door'. Not 'clean the flat' — 'do one washing-up bowl'. The size of the step matters less than the momentum it creates.",
      "Aim for a mix of pleasure activities (things you enjoy) and mastery activities (things that give a sense of doing). Both lift mood — and both are what depression steals first.",
    ],
    flashcards: [
      { front: "🌤 The core idea", back: "Do first, feel second. Waiting to 'feel like it' keeps low mood stuck." },
      { front: "Start size", back: "Absurdly small. Trainers by the door beats 'go for a run'." },
      { front: "Two types", back: "Pleasure (things you enjoy) + Mastery (things that give a sense of doing)." },
      { front: "Why it works", back: "Action creates evidence that you can do things — which shifts mood and thought." },
    ],
    worksheetPrompt: "List three tiny actions you could do today — one pleasure, one mastery, one movement. Which will you actually do?",
    scenario: {
      prompt: "You've been in bed all morning and feel awful. What's the behavioural-activation move?",
      options: [
        { text: "Wait until you feel motivated, then get up.", correct: false, feedback: "That's exactly the trap. Motivation follows action, not the other way round." },
        { text: "Sit up. Feet on floor. Glass of water. That's the whole task.", correct: true, feedback: "Yes. Absurdly small on purpose — the win is the doing, not the size." },
        { text: "Force yourself to do a full workout.", correct: false, feedback: "Too big. Sets you up to fail — and the failure becomes evidence for the depressive story." },
        { text: "Scroll on your phone until you feel better.", correct: false, feedback: "Not behavioural activation — that's the avoidance loop." },
      ],
    },
    reflectionPrompt: "What's one absurdly-small action that would tell your mind 'I can do things'? Can you do it today?",
  },
  {
    id: "exposure",
    title: "Exposure",
    emoji: "🚪",
    summary: "Facing what you avoid — gently, gradually, on your terms.",
    learn: [
      "Avoidance shrinks life. Every time you avoid something scary, the fear grows a little stronger. Exposure reverses that: you approach the thing gently, and the fear starts to shrink.",
      "The rule is graded — never flooding. Build a ladder from least scary to most scary. Start at the bottom. Stay long enough that the anxiety peaks and drops. Then move up.",
      "For trauma, this needs to be paced carefully and often with a therapist. Exposure without safety is retraumatising. But small, chosen exposures — sending the message, going to the shop, saying the sentence — are healing.",
    ],
    flashcards: [
      { front: "🚪 What it does", back: "Approach what you avoid so fear shrinks instead of growing." },
      { front: "Avoidance", back: "Feels safe short-term. Grows fear long-term." },
      { front: "Rule", back: "Graded exposure — never flooding. Bottom of the ladder first." },
      { front: "Trauma caveat", back: "Trauma exposure work is usually paced with a therapist. Small chosen exposures at home are still valuable." },
    ],
    worksheetPrompt: "Pick something you've been avoiding. Build a 5-rung ladder from least to most scary. What's rung 1?",
    scenario: {
      prompt: "You've avoided phone calls for months. What's a good rung-1 exposure?",
      options: [
        { text: "Ring five people you don't know today.", correct: false, feedback: "That's flooding — too big, too fast. Sets you up to fail." },
        { text: "Say the phrase 'hello, it's me' out loud in your room.", correct: true, feedback: "Yes — tiny, safe, still an approach. That's rung 1." },
        { text: "Delete your phone.", correct: false, feedback: "That's avoidance dressed as a solution — the fear will grow." },
        { text: "Ring your bank about a complaint.", correct: false, feedback: "That's probably rung 4 or 5. Start smaller." },
      ],
    },
    reflectionPrompt: "What's one small approach you could make this week toward something you've been avoiding?",
  },
  {
    id: "activity-scheduling",
    title: "Activity Scheduling",
    emoji: "📅",
    summary: "Plan meaningful moments before your mood decides for you.",
    learn: [
      "Low mood tells you nothing is worth doing. Activity scheduling gets ahead of that voice by deciding — in advance, when you have a little more clarity — what small things go in your week.",
      "Block them in as if they were appointments. 'Tuesday 4pm: walk to the park.' 'Sunday morning: coffee with Ash.' The decision is made when the depressive voice isn't the one holding the pen.",
      "You won't feel like doing them when the time comes. Do them anyway — that's the whole point. Then rate how it felt afterwards. The evidence usually surprises you.",
    ],
    flashcards: [
      { front: "📅 What it is", back: "Planning small meaningful activities in advance — before low mood decides." },
      { front: "How", back: "Block them like appointments. Specific day, specific time." },
      { front: "The rule", back: "Do it even when you don't feel like it. The doing is the point." },
      { front: "Track it", back: "Rate mood before and after. Evidence beats prediction." },
    ],
    worksheetPrompt: "Write out the next 3 days. In each, book ONE small activity — pleasure or mastery — with a time.",
    scenario: {
      prompt: "You scheduled a walk for 4pm. It's 4pm and you feel awful. What's the CBT move?",
      options: [
        { text: "Skip it — you'll do it when you feel better.", correct: false, feedback: "That's the depressive voice writing the schedule. The whole point is not to let it." },
        { text: "Do a shorter version anyway — ten minutes counts.", correct: true, feedback: "Yes. The doing matters more than the size. Ten minutes is a full activity." },
        { text: "Cancel everything for the week.", correct: false, feedback: "That's low mood expanding to fill the diary. Guard the schedule." },
        { text: "Force yourself to walk for an hour.", correct: false, feedback: "Rigid all-or-nothing. Ten minutes is enough today." },
      ],
    },
    reflectionPrompt: "Which activity from your schedule surprised you — turned out better (or worse) than predicted?",
  },
  {
    id: "goal-setting",
    title: "Goal Setting",
    emoji: "🎯",
    summary: "SMART goals that stretch you without breaking you.",
    learn: [
      "A goal you never reach isn't ambition — it's a slow drip of failure evidence. Good CBT goals are specific, measurable, achievable, relevant, and time-bound (SMART).",
      "The most common mistake: goals that are too big. 'Get fit' fails. 'Walk twice a week for 20 minutes for the next month' works. Small, boring, doable.",
      "Break long goals into weekly steps, and weekly steps into daily actions. Celebrate the tiny wins — that's the fuel that keeps the whole thing going.",
    ],
    flashcards: [
      { front: "🎯 SMART", back: "Specific · Measurable · Achievable · Relevant · Time-bound." },
      { front: "Common mistake", back: "Too big. 'Get fit' fails. 'Walk 20 mins twice a week' works." },
      { front: "Breakdown", back: "Long goal → weekly step → daily action. All three, in writing." },
      { front: "Fuel", back: "Celebrate the tiny wins. That's the momentum." },
    ],
    worksheetPrompt: "Pick one goal. Rewrite it in SMART form. Then write this week's step, and today's action.",
    scenario: {
      prompt: "'I want to feel less anxious.' Which is a SMART version?",
      options: [
        { text: "'Never feel anxious again.'", correct: false, feedback: "Not achievable — anxiety is part of being human." },
        { text: "'Practise 5 minutes of breathwork daily for 4 weeks and rate anxiety weekly 0–10.'", correct: true, feedback: "Yes — specific, measurable, achievable, relevant, time-bound." },
        { text: "'Get better mentally.'", correct: false, feedback: "Not specific or measurable. Nothing to hold onto." },
        { text: "'Do all the anxiety skills at once.'", correct: false, feedback: "Not achievable. Pick one skill and practise it." },
      ],
    },
    reflectionPrompt: "What's one small, specific, doable goal for this week? What's the smallest first step?",
  },
  {
    id: "problem-solving",
    title: "Problem Solving",
    emoji: "🧩",
    summary: "A 5-step process for when the worry loop is spinning.",
    learn: [
      "When you can't stop worrying about something, it's often because the mind is trying to solve a problem but doesn't have a process. Structured problem solving gives it one.",
      "The five steps: 1) Define the problem specifically. 2) Brainstorm every possible solution (no filter). 3) Weigh pros and cons of the top three. 4) Choose one and plan the first step. 5) Do it, then review.",
      "The key is 'specifically'. 'I'm stressed at work' isn't solvable. 'I have three tasks due Friday and don't know which to start' is. Vague problems keep worry alive; specific ones can be solved.",
    ],
    flashcards: [
      { front: "🧩 5 steps", back: "Define → Brainstorm → Weigh top 3 → Choose + plan step 1 → Do + review." },
      { front: "Rule of step 1", back: "Specific. Vague problems can't be solved — they just keep worry alive." },
      { front: "Rule of step 2", back: "No filter. Even bad ideas widen the field." },
      { front: "Rule of step 5", back: "Do something. Then review. Perfect plans don't beat imperfect action." },
    ],
    worksheetPrompt: "Pick a worry that's on repeat. Walk it through all 5 steps in writing. What's step 5's action?",
    scenario: {
      prompt: "'I feel overwhelmed by work.' What's the CBT step 1?",
      options: [
        { text: "Quit the job.", correct: false, feedback: "That's jumping to step 5 without defining the problem." },
        { text: "Get more specific — which tasks, which deadlines, which parts?", correct: true, feedback: "Yes. Vague can't be solved. Specific can." },
        { text: "Try to feel less overwhelmed.", correct: false, feedback: "That's the feeling, not the problem. Look at what's underneath." },
        { text: "Ruminate on why it's always like this.", correct: false, feedback: "That's the loop — the whole reason to use this skill." },
      ],
    },
    reflectionPrompt: "What worry has been on repeat lately? What's the specific, solvable version of it?",
  },
  {
    id: "building-healthy-habits",
    title: "Building Healthy Habits",
    emoji: "🌱",
    summary: "Tiny, consistent actions beat big bursts every time.",
    learn: [
      "Habits are behaviours that have become automatic. They form when a cue (trigger) reliably leads to an action, and the action gives a reward. Build the loop — the habit follows.",
      "Two rules that make almost every habit stick. First: start absurdly small. Two press-ups, one page, one deep breath. Small survives bad days. Second: attach the new habit to an existing one ('after I brush my teeth, I…').",
      "Expect to miss. Missing once is normal; missing twice is where habits die. The rule of thumb: never miss twice.",
    ],
    flashcards: [
      { front: "🌱 The loop", back: "Cue → Action → Reward. Build the loop; the habit follows." },
      { front: "Rule 1", back: "Absurdly small. Two press-ups, one page. Small survives bad days." },
      { front: "Rule 2", back: "Attach to an existing habit. 'After I brush my teeth, I…'" },
      { front: "Rule 3", back: "Never miss twice. One miss is normal; two is where habits die." },
    ],
    worksheetPrompt: "Pick one habit you want. Design its cue (when?), action (what tiny thing?), and reward (what's the small pleasure?).",
    scenario: {
      prompt: "You want to start journaling daily. Best design?",
      options: [
        { text: "'Write 3 pages every night before bed.'", correct: false, feedback: "Too big — dies on the first hard day." },
        { text: "'After my morning tea, write one sentence in a notebook I keep by the kettle.'", correct: true, feedback: "Yes — tiny, cued, easy to keep going even on bad days." },
        { text: "'Journal when I feel like it.'", correct: false, feedback: "No cue, no habit. Motivation isn't reliable enough." },
        { text: "'Buy a beautiful notebook first.'", correct: false, feedback: "That's shopping, not habit-building." },
      ],
    },
    reflectionPrompt: "Which small habit would most support you this month? What existing habit can you attach it to?",
  },
];

// ─────────────────────────────────────────────────────────────
// 5. CORE BELIEFS & SELF-ESTEEM
// ─────────────────────────────────────────────────────────────
const coreBeliefs: CbtSkill[] = [
  {
    id: "core-beliefs",
    title: "Core Beliefs",
    emoji: "🌳",
    summary: "The deep beliefs about yourself that most other thoughts grow from.",
    learn: [
      "Core beliefs are the deep, usually unspoken beliefs about yourself, others, and the world. 'I'm unlovable.' 'People leave.' 'The world isn't safe.' They're often formed early, before you had words.",
      "You don't usually feel them directly — you feel the automatic thoughts that grow from them. Repeatedly. In lots of different situations. That repetition is the clue that a core belief is running underneath.",
      "You don't change them by arguing. You change them by gathering, over time, small pieces of evidence that don't fit — and letting yourself register that evidence. This is slow work. It's also possible.",
    ],
    flashcards: [
      { front: "🌳 What they are", back: "Deep, usually unspoken beliefs about self, others, world — often formed pre-verbally." },
      { front: "How to spot one", back: "The same automatic thought showing up across many different situations." },
      { front: "Common ones", back: "'I'm unlovable.' 'I'm not enough.' 'People leave.' 'The world isn't safe.'" },
      { front: "How they shift", back: "Not by argument. By slowly gathering — and letting yourself notice — evidence that doesn't fit." },
    ],
    worksheetPrompt: "Complete: 'I am ______.' 'Other people are ______.' 'The world is ______.' What do you notice?",
    scenario: {
      prompt: "You keep having the thought 'I'm too much' — at work, at home, with friends. What's the CBT reading?",
      options: [
        { text: "It must be true — the thought is that consistent.", correct: false, feedback: "Consistency isn't evidence — it's usually the fingerprint of a core belief." },
        { text: "It's probably an old core belief showing up in lots of situations.", correct: true, feedback: "Yes. Same thought in many settings = core belief, not fact." },
        { text: "Everyone thinks that sometimes.", correct: false, feedback: "Maybe — but the repetition is the important clue here." },
        { text: "Best to ignore it.", correct: false, feedback: "Ignoring keeps it in the shadows. Naming it starts to loosen it." },
      ],
    },
    reflectionPrompt: "What core belief do you suspect is running under your most stuck thoughts? Where might it have started?",
  },
  {
    id: "self-critical-thoughts",
    title: "Self-Critical Thoughts",
    emoji: "🗣",
    summary: "The inner voice that tears you down — and how to answer it.",
    learn: [
      "Self-critical thoughts are the inner voice that's harsher with you than you'd ever be with anyone else. 'You're pathetic.' 'You always mess this up.' 'What's wrong with you?'",
      "That voice usually isn't yours — it's an echo of a critical adult, a bullying peer, or a survival strategy that tried to keep you safe by attacking first. It made sense once. It doesn't now.",
      "You don't have to argue with it or silence it. Two responses work better: name it ('there's the inner critic') and answer it with the voice of someone who loves you ('that's not how I'd speak to a friend').",
    ],
    flashcards: [
      { front: "🗣 The pattern", back: "An inner voice harsher with you than you'd ever be with anyone else." },
      { front: "Where it's from", back: "Usually an echo — a critical adult, a bully, or a self-attack strategy that once kept you safe." },
      { front: "Response 1", back: "Name it: 'There's the inner critic.' Naming loosens." },
      { front: "Response 2", back: "Answer with the friend voice: 'That's not how I'd speak to someone I love.'" },
    ],
    worksheetPrompt: "Write down one thing your inner critic said today. Now write what a loving friend would say to you instead.",
    scenario: {
      prompt: "Your inner critic says: 'You're such a mess.' What's the CBT response?",
      options: [
        { text: "'You're right — I am.'", correct: false, feedback: "That's collusion with the critic — not the goal." },
        { text: "'Shut up.'", correct: false, feedback: "Fighting the critic usually makes it louder." },
        { text: "'There's the critic. That's not how I'd speak to a friend.'", correct: true, feedback: "Yes — name it, redirect to the friend voice." },
        { text: "Ignore it and hope it stops.", correct: false, feedback: "Ignoring keeps it in the shadows. Naming it defuses it." },
      ],
    },
    reflectionPrompt: "Whose voice does your inner critic sound like? What would you like to say back to it?",
  },
  {
    id: "self-compassion",
    title: "Building Self-Compassion",
    emoji: "🤲",
    summary: "Treating yourself the way you'd treat someone you love.",
    learn: [
      "Self-compassion isn't self-pity or self-indulgence. It's speaking to yourself, in hard moments, the way you'd speak to a friend going through the same thing.",
      "Three parts: kindness (warmth instead of criticism), common humanity (this is hard AND many people struggle with it), and mindfulness (noticing the pain without exaggerating or minimising).",
      "It feels awkward at first — especially if you were taught that being hard on yourself is what makes you grow. It doesn't. Compassion is what makes people brave enough to try again.",
    ],
    flashcards: [
      { front: "🤲 Three parts", back: "Kindness · Common humanity · Mindfulness of the pain." },
      { front: "Not this", back: "Not self-pity. Not self-indulgence. Not letting yourself off." },
      { front: "The test", back: "'Would I say this to a friend in the same spot?' If not, rewrite." },
      { front: "Why it works", back: "Compassion is what makes people brave enough to try again — not harshness." },
    ],
    worksheetPrompt: "Place a hand on your chest. Write yourself the words a beloved friend would say about what you're going through right now.",
    scenario: {
      prompt: "You made a big mistake at work. What's the compassionate response?",
      options: [
        { text: "'I'm an idiot — I should have known better.'", correct: false, feedback: "That's the critic. Not compassion." },
        { text: "'It's fine, it doesn't matter.'", correct: false, feedback: "That's minimisation — dismissing your own pain isn't compassion." },
        { text: "'This is really painful. Most people make mistakes at work. What do I need right now?'", correct: true, feedback: "Yes — all three parts: mindfulness, common humanity, kindness." },
        { text: "'I need to punish myself so it doesn't happen again.'", correct: false, feedback: "Harshness doesn't prevent mistakes — it just adds shame on top." },
      ],
    },
    reflectionPrompt: "What would it take to speak to yourself the way you speak to the people you love?",
  },
  {
    id: "self-esteem",
    title: "Self-Esteem",
    emoji: "⭐",
    summary: "Built by evidence, not by affirmations you don't believe.",
    learn: [
      "Self-esteem isn't affirmations you repeat until you believe them. Most trauma survivors' minds reject affirmations — they feel like a lie. Real self-esteem is built by evidence.",
      "You gather evidence in two ways. First: notice what you actually did today, however small. Second: notice what you're actually like — the qualities other people would name in you.",
      "Over weeks and months, evidence stacks up. The story your mind tells about you starts to catch up with the reality of who you are and what you do.",
    ],
    flashcards: [
      { front: "⭐ Not this", back: "Not affirmations. Most survivors' minds reject them as lies." },
      { front: "The alternative", back: "Evidence. What you actually did. What you actually are." },
      { front: "How to gather", back: "Daily: three things you did or three qualities you showed." },
      { front: "The result", back: "The story catches up with the reality. Slowly. Reliably." },
    ],
    worksheetPrompt: "List three things you did today. Then three qualities you showed. This is the raw evidence.",
    scenario: {
      prompt: "You've decided to build self-esteem. Which practice actually works?",
      options: [
        { text: "Say 'I am amazing' to the mirror every morning.", correct: false, feedback: "If you don't believe it, your mind rejects it. Not evidence." },
        { text: "Each evening, write three things you did or qualities you showed — however small.", correct: true, feedback: "Yes — that's evidence-building. Slow, but real." },
        { text: "Wait until you feel good about yourself, then act on it.", correct: false, feedback: "That's the trap — feelings follow evidence, not the other way round." },
        { text: "Compare yourself to others who seem confident.", correct: false, feedback: "Comparison is the opposite of building your own evidence." },
      ],
    },
    reflectionPrompt: "What's one quality you know you have — even quietly? What evidence supports it?",
  },
  {
    id: "positive-data-log",
    title: "Positive Data Log",
    emoji: "📓",
    summary: "A running record of evidence that contradicts an old belief.",
    learn: [
      "A positive data log is a notebook — physical or digital — where you record small moments that don't fit a negative core belief. It's designed to counter the mind's habit of filtering them out.",
      "First, pick the belief you want to soften ('I'm unlovable'). Then each day, write down any small piece of evidence, however tiny, that doesn't fit. 'My sister rang me.' 'A stranger smiled.' 'A colleague thanked me.'",
      "Do this for weeks, not days. The point isn't any single entry — it's the cumulative weight of noticing. Over time, the mind's filter starts letting different information through.",
    ],
    flashcards: [
      { front: "📓 What it is", back: "A running record of small evidence that contradicts a negative core belief." },
      { front: "Why", back: "Countering the mind's habit of filtering these moments out entirely." },
      { front: "How", back: "Pick the belief. Then daily: any tiny evidence that doesn't fit." },
      { front: "Timescale", back: "Weeks, not days. Cumulative weight is what shifts belief." },
    ],
    worksheetPrompt: "Pick one negative belief to soften. Write down 3 pieces of evidence from the last week that don't fit it.",
    scenario: {
      prompt: "Belief: 'I'm not good at friendships.' Which entry belongs in the positive data log?",
      options: [
        { text: "'My friend didn't reply to my message.'", correct: false, feedback: "That's evidence FOR the belief. This log is for evidence against." },
        { text: "'Two people asked how I was this week — someone laughed at my joke.'", correct: true, feedback: "Yes — small, concrete, doesn't fit the belief. Perfect." },
        { text: "'I should be a better friend.'", correct: false, feedback: "That's a 'should', not evidence." },
        { text: "'I'm hopeless.'", correct: false, feedback: "That's another negative belief stacked on top." },
      ],
    },
    reflectionPrompt: "Which core belief would benefit most from a positive data log? Can you start it today?",
  },
];

// ─────────────────────────────────────────────────────────────
// 6. EVERYDAY CBT TOOLS
// ─────────────────────────────────────────────────────────────
const everydayTools: CbtSkill[] = [
  {
    id: "worry-time",
    title: "Worry Time",
    emoji: "⏰",
    summary: "A scheduled 15 minutes where the worry gets your full attention.",
    learn: [
      "Trying to stop worrying makes worry louder. Worry time flips it: you schedule 15 minutes a day when the worry gets your full attention — and outside that window, you gently postpone.",
      "When a worry pops up during the day, jot it down and tell yourself 'this belongs in worry time.' At the scheduled time, sit with the list. Most worries have already faded; the rest get properly examined.",
      "It sounds counter-intuitive but works because the mind stops fighting itself. The worry gets its slot, so it doesn't have to shout for attention all day.",
    ],
    flashcards: [
      { front: "⏰ What it is", back: "A scheduled 15 minutes a day where worry gets your full attention." },
      { front: "During the day", back: "Jot down the worry. Say: 'This belongs in worry time.' Move on." },
      { front: "At worry time", back: "Sit with the list. Most have faded. The rest get proper examination." },
      { front: "Why it works", back: "Mind stops fighting itself. Worry gets its slot, so it doesn't have to shout." },
    ],
    worksheetPrompt: "Choose your worry-time slot. Where? What time? How will you remind yourself outside it?",
    scenario: {
      prompt: "It's 11am. You planned worry time for 6pm. A big worry pops up. What now?",
      options: [
        { text: "Drop everything and worry about it now.", correct: false, feedback: "That's the whole thing worry time is trying to interrupt." },
        { text: "Suppress it and pretend it isn't there.", correct: false, feedback: "Suppression makes worry louder. Not the skill either." },
        { text: "Jot it on the worry list. 'This belongs in worry time. See you at 6.'", correct: true, feedback: "Yes — acknowledge, postpone, move on. That's exactly it." },
        { text: "Cancel worry time — you'll do it whenever.", correct: false, feedback: "Then the whole system collapses. The slot has to be a slot." },
      ],
    },
    reflectionPrompt: "How much of your day is currently spent worrying? What would you do with that time back?",
  },
  {
    id: "gratitude-practice",
    title: "Gratitude Practice",
    emoji: "🌸",
    summary: "Not toxic positivity — a training in noticing what's actually here.",
    learn: [
      "Real gratitude practice isn't 'ignore the bad and think about the good'. It's training the mind to notice what's actually here — because the depressive filter deletes most of it.",
      "The evidence-based version: three specific things, from the last 24 hours, that you're glad about. Specificity is the key. Not 'my family' — 'the way my dog leaned into me this morning.'",
      "You don't need to feel grateful for hard things. You just need to notice the small, real, good things the mind was about to skip past.",
    ],
    flashcards: [
      { front: "🌸 What it is", back: "Training the mind to notice what's actually here — countering the depressive filter." },
      { front: "Not this", back: "Not forced positivity. Not being grateful for the hard things." },
      { front: "The evidence-based version", back: "Three specific things, from the last 24 hours, that you're glad about." },
      { front: "Rule", back: "Specific. Not 'my family' but 'the way my dog leaned into me this morning'." },
    ],
    worksheetPrompt: "Write three specific things from the last 24 hours that you're glad about — sensory, concrete, small is fine.",
    scenario: {
      prompt: "Which gratitude entry is the CBT-style one?",
      options: [
        { text: "'I'm grateful for everything in my life.'", correct: false, feedback: "Too vague. The mind doesn't register vague." },
        { text: "'The steam off my tea this morning.'", correct: true, feedback: "Yes — specific, sensory, small. That's the practice." },
        { text: "'I should be more grateful.'", correct: false, feedback: "That's a should stacked on shame, not gratitude." },
        { text: "'Others have it worse.'", correct: false, feedback: "That's comparison, not gratitude — and it usually adds guilt." },
      ],
    },
    reflectionPrompt: "What tiny, specific thing from today were you about to skip past — but is worth noticing?",
  },
  {
    id: "coping-statements",
    title: "Coping Statements",
    emoji: "💬",
    summary: "Short, believable sentences you can lean on in hard moments.",
    learn: [
      "A coping statement is a short phrase you say to yourself in a difficult moment. Not affirmations you don't believe — sentences that acknowledge the hard AND remind you of what's true.",
      "Good ones sound like: 'This is really hard, AND I've done hard things before.' 'This feeling is uncomfortable, AND it will pass.' 'I don't have to solve this today.' 'I am safe in this moment.'",
      "Write your own — three to five. Keep them somewhere you'll see them (phone lock screen, front of journal, mirror). Read them when things feel too big.",
    ],
    flashcards: [
      { front: "💬 What they are", back: "Short, believable phrases to lean on in hard moments." },
      { front: "The shape", back: "'This is hard AND [what's also true].'" },
      { front: "The test", back: "Do you believe it? If not, rewrite until you do." },
      { front: "Keep them close", back: "Phone lock screen. Front of journal. Somewhere you'll see them." },
    ],
    worksheetPrompt: "Write 3–5 coping statements you actually believe. Which one will you carry with you this week?",
    scenario: {
      prompt: "Which is a good coping statement?",
      options: [
        { text: "'Everything is fine.'", correct: false, feedback: "If it isn't fine, you won't believe it. Won't work." },
        { text: "'This is hard, AND I've come through hard before.'", correct: true, feedback: "Yes — acknowledges reality AND holds a truth. Believable." },
        { text: "'I should just get over it.'", correct: false, feedback: "That's the inner critic dressed up." },
        { text: "'I am invincible.'", correct: false, feedback: "Feels like a lie. Your mind will reject it." },
      ],
    },
    reflectionPrompt: "What coping statement do you wish someone had said to you when you were younger?",
  },
  {
    id: "behaviour-experiments",
    title: "Behaviour Experiments",
    emoji: "🧪",
    summary: "Test your predictions in real life. Reality usually surprises you.",
    learn: [
      "A behaviour experiment tests an anxious prediction in the real world. Prediction: 'If I speak in the meeting, everyone will judge me.' Experiment: speak once. Observe what actually happens.",
      "Structure: 1) Write the prediction and rate how strongly you believe it (0–100%). 2) Design the smallest experiment to test it. 3) Do it. 4) Write what actually happened. 5) Rate the belief again.",
      "Almost always, the belief drops. Sometimes it goes back up later — that's fine. Do another experiment. Evidence stacks up, and stacked evidence is what actually rewires a belief.",
    ],
    flashcards: [
      { front: "🧪 What it is", back: "Testing an anxious prediction in the real world instead of arguing with it." },
      { front: "5 steps", back: "Predict → Rate belief % → Do smallest test → Note what happened → Rate belief again." },
      { front: "Why it beats thinking about it", back: "Real evidence rewires belief in a way logic can't." },
      { front: "One isn't enough", back: "The belief may rebound. Stack experiments over time." },
    ],
    worksheetPrompt: "Design one experiment. Prediction: ___. Belief before: __%. What will I do? What actually happened? Belief after: __%.",
    scenario: {
      prompt: "Prediction: 'If I say no to my sister, she'll be furious for weeks.' Best experiment?",
      options: [
        { text: "Say no to everyone in your life, all at once.", correct: false, feedback: "Too big. Sets you up for chaos, not evidence." },
        { text: "Never say no and stay safe.", correct: false, feedback: "That's avoidance — the whole thing this skill is designed to interrupt." },
        { text: "Say no to one small thing this week. Note what actually happens.", correct: true, feedback: "Yes — smallest safe test, real evidence, then rate the belief again." },
        { text: "Ask her in advance if she'd be furious.", correct: false, feedback: "That's asking her to predict too. The experiment is doing it and observing." },
      ],
    },
    reflectionPrompt: "Which anxious prediction has ruled your behaviour longest? What tiny experiment could test it?",
  },
  {
    id: "relapse-prevention",
    title: "Relapse Prevention",
    emoji: "🛡",
    summary: "A plan for what to do when it gets hard again — because it will.",
    learn: [
      "Progress in CBT isn't a straight line. Hard weeks come back. Old patterns show up. That's normal — it isn't a sign the work didn't take. It's a sign you're a human being living a real life.",
      "A relapse prevention plan is written when you're doing okay, for when you're not. It includes: your early warning signs, the skills that most reliably help, the people you can reach out to, and one gentle sentence to your future struggling self.",
      "Keep it somewhere you'll find it. Because when things dip, the mind hides everything that helps. A plan on paper is the way through.",
    ],
    flashcards: [
      { front: "🛡 What it is", back: "A written plan for when things get hard again — because they will." },
      { front: "When to write it", back: "When you're doing okay, for when you're not." },
      { front: "What to include", back: "Early warning signs · Skills that help · People to reach · A sentence to future you." },
      { front: "Why paper", back: "When things dip, the mind hides what helps. Paper doesn't." },
    ],
    worksheetPrompt: "Draft your plan: My early warning signs are… The skills that most help me are… The people I can reach are… My sentence to my struggling self is…",
    scenario: {
      prompt: "You've had a great few weeks of CBT progress. What does the skill say to do?",
      options: [
        { text: "Assume it's fixed. Stop the work.", correct: false, feedback: "That's the exact moment relapse tends to sneak in." },
        { text: "Write a relapse prevention plan now, while you can see clearly.", correct: true, feedback: "Yes — write it while you're steady, for the days when you aren't." },
        { text: "Worry about when it'll come back.", correct: false, feedback: "That's the anxious version. Prepare, don't ruminate." },
        { text: "Tell everyone you're cured.", correct: false, feedback: "Growth isn't a straight line. This just adds pressure." },
      ],
    },
    reflectionPrompt: "What's the sentence you'd want your struggling self to read when things next get hard?",
  },
];

// ─────────────────────────────────────────────────────────────
// MODULES
// ─────────────────────────────────────────────────────────────
export const CBT_MODULES: CbtModule[] = [
  {
    id: "foundations",
    label: "CBT Foundations",
    emoji: "🧭",
    tagline: "Understand how CBT works",
    intro: "Learn how thoughts, emotions, behaviours, and physical sensations influence one another — the ground the rest of CBT is built on.",
    theme: "sky",
    skills: foundations,
  },
  {
    id: "thinking-patterns",
    label: "Thinking Patterns",
    emoji: "💭",
    tagline: "Recognise unhelpful thinking habits",
    intro: "Learn to notice the cognitive distortions that quietly drive anxiety, low mood, shame, and self-criticism.",
    theme: "peach",
    skills: thinkingPatterns,
  },
  {
    id: "challenging-thoughts",
    label: "Challenging Thoughts",
    emoji: "🔄",
    tagline: "Question unhelpful beliefs with curiosity",
    intro: "Replace automatic assumptions with more balanced and compassionate thinking — one thought at a time.",
    theme: "sage",
    skills: challengingThoughts,
  },
  {
    id: "behaviour-change",
    label: "Behaviour Change",
    emoji: "🚶",
    tagline: "Change actions to change feelings",
    intro: "Practical strategies that improve mood, reduce avoidance, and build confidence through small, doable steps.",
    theme: "lavender",
    skills: behaviourChange,
  },
  {
    id: "core-beliefs",
    label: "Core Beliefs & Self-Esteem",
    emoji: "🌳",
    tagline: "Understand deeper beliefs about yourself",
    intro: "Explore the beliefs shaped by life experiences and begin gently building healthier ones — with evidence, not affirmations.",
    theme: "gold",
    skills: coreBeliefs,
  },
  {
    id: "everyday-tools",
    label: "Everyday CBT Tools",
    emoji: "🛠",
    tagline: "Quick techniques you can use daily",
    intro: "Small, portable practices — worry time, gratitude, coping statements, behaviour experiments, and a plan for hard days.",
    theme: "sky",
    skills: everydayTools,
  },
];

// Helpers
export function findModule(id: string): CbtModule | undefined {
  return CBT_MODULES.find((m) => m.id === id);
}

export function findSkill(
  moduleId: string,
  skillId: string,
): { module: CbtModule; skill: CbtSkill; index: number } | undefined {
  const module = findModule(moduleId);
  if (!module) return undefined;
  const index = module.skills.findIndex((s) => s.id === skillId);
  if (index < 0) return undefined;
  return { module, skill: module.skills[index], index };
}

/** Returns the next skill in the same module, or the first skill of the next module. */
export function nextSkill(
  moduleId: string,
  skillId: string,
): { moduleId: CbtModuleId; skillId: string } | undefined {
  const modIdx = CBT_MODULES.findIndex((m) => m.id === moduleId);
  if (modIdx < 0) return undefined;
  const module = CBT_MODULES[modIdx];
  const skillIdx = module.skills.findIndex((s) => s.id === skillId);
  if (skillIdx < 0) return undefined;
  if (skillIdx + 1 < module.skills.length) {
    return { moduleId: module.id, skillId: module.skills[skillIdx + 1].id };
  }
  const nextMod = CBT_MODULES[modIdx + 1];
  if (nextMod) {
    return { moduleId: nextMod.id, skillId: nextMod.skills[0].id };
  }
  return undefined;
}

export function totalSkillCount(): number {
  return CBT_MODULES.reduce((n, m) => n + m.skills.length, 0);
}
